import { useRef } from 'react';
import { v4 } from 'uuid';
import { useThrowError } from './useThrowError';
import { isDevelopmentEnvironment } from '@/utils/url';

export function useNethone() {
  const antifraudId = useRef<string>('');
  const isScriptLoaded = useRef<boolean>(false);
  const { throwError } = useThrowError();

  const cleanupNethoneScript = () => {
    const existingScript = document.getElementById('c2984adb-af27-48b1-b56d-771ca71b0c34');
    if (existingScript) {
      existingScript.remove();
    }

    isScriptLoaded.current = false;
    antifraudId.current = '';
  };

  const loadNethoneScript = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = src;
      script.async = true;
      script.crossOrigin = 'use-credentials';
      script.id = 'c2984adb-af27-48b1-b56d-771ca71b0c34';

      script.onload = () => {
        resolve();
      };

      script.onerror = () => {
        reject(throwError('Failed to load Nethone script.'));
      };

      document.body.appendChild(script);
    });
  };

  const initializeDftp = async (id: string) => {
    if (antifraudId.current != id) {
      antifraudId.current = id;
    }

    if (!isScriptLoaded.current) {
      isScriptLoaded.current = true;
    }

    // Bypass Nethone em ambiente de desenvolvimento
    if (isDevelopmentEnvironment()) {
      return;
    }

    try {
      const nethoneScriptSrc = import.meta.env.VITE_NETHONE_SCRIPT_SRC as string;
      await loadNethoneScript(nethoneScriptSrc);
      //@ts-ignore
      window.dftp.init({
        attemptReference: id,
        sensitiveFields: ['number', 'cvc']
      });
    } catch (error) {
      throw throwError(`Erro ao inicializar o dftp: ${error}`);
    }
  };

  const initializateNethoneScript = async () => {
    cleanupNethoneScript();

    try {
      const antifraudRefId = v4();
      await initializeDftp(antifraudRefId);
    } catch (error) {
      throw throwError(`Falha na inicialização do antifraude: ${error}`);
    }
  };

  const completeProfile = async () => {
    // Bypass Nethone em ambiente de desenvolvimento
    if (isDevelopmentEnvironment()) {
      return;
    }

    try {
      //@ts-ignore
      await window.dftp.profileCompleted();
    } catch (error) {
      throw throwError(`Falha na validação do perfil Nethone: ${error}`);
    }
  };

  const validateNethoneProfileCompletion = async () => {
    // Bypass completo em ambiente de desenvolvimento
    if (isDevelopmentEnvironment()) {
      return;
    }

    if (!isScriptLoaded.current) {
      try {
        await initializateNethoneScript();
      } catch (error) {
        throw throwError(`Falha na inicialização do Nethone: ${error}`);
      }
    }

    //@ts-ignore
    if (window.dftp) {
      await completeProfile();
    } else {
      try {
        await initializateNethoneScript();
        //@ts-ignore
        if (window.dftp) {
          await completeProfile();
        }
      } catch (error) {
        throw throwError(`Falha na inicialização do Nethone: ${error}`);
      }
    }
  };

  return {
    cleanupNethoneScript,
    initializateNethoneScript,
    antifraudId: antifraudId.current,
    validateNethoneProfileCompletion
  };
}
